import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  type RiskReductionMeasure,
  type StandardApplicability,
  useListStandardApplicability,
  useUpdateRiskReductionMeasure,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { toRiskReductionMeasureUpdateInput } from './riskReductionMeasuresUtils'

type EditRiskReductionMeasureStandardConformityDialogProps = {
  isOpen: boolean
  onClose: () => void
  complianceId: string
  riskReductionMeasure: RiskReductionMeasure
}

function getAcceptedStandards(
  applicability: StandardApplicability[] | undefined
) {
  return applicability?.filter((row) => row.isAccepted === true) ?? []
}

export function EditRiskReductionMeasureStandardConformityDialog({
  isOpen,
  onClose,
  complianceId,
  riskReductionMeasure,
}: EditRiskReductionMeasureStandardConformityDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const updateMutation = useUpdateRiskReductionMeasure(complianceId)
  const { data: applicability = [], isLoading: isLoadingApplicability } =
    useListStandardApplicability(isOpen ? complianceId : undefined)

  const acceptedStandards = useMemo(
    () => getAcceptedStandards(applicability),
    [applicability]
  )

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [noteDraft, setNoteDraft] = useState('')

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setSelectedIds(
      riskReductionMeasure.standardConformity.standards.map((standard) => standard.id)
    )
    setNoteDraft(riskReductionMeasure.standardConformity.note ?? '')
  }, [isOpen, riskReductionMeasure.standardConformity])

  const isSaving = updateMutation.isPending

  const currentSelectedIds = useMemo(
    () =>
      riskReductionMeasure.standardConformity.standards.map((standard) => standard.id),
    [riskReductionMeasure.standardConformity.standards]
  )
  const currentNote =
    riskReductionMeasure.standardConformity.note?.trim() ?? ''

  const hasChanges = useMemo(() => {
    const trimmedNote = noteDraft.trim()
    const selectedChanged =
      selectedIds.length !== currentSelectedIds.length ||
      selectedIds.some((id) => !currentSelectedIds.includes(id))
    const noteChanged = trimmedNote !== currentNote
    return selectedChanged || noteChanged
  }, [currentNote, currentSelectedIds, noteDraft, selectedIds])

  const handleClose = useCallback(() => {
    if (isSaving) {
      return
    }
    onClose()
  }, [isSaving, onClose])

  const toggleStandard = (standardId: string, checked: boolean) => {
    setSelectedIds((previous) => {
      if (checked) {
        if (previous.includes(standardId)) {
          return previous
        }
        return [...previous, standardId]
      }
      return previous.filter((id) => id !== standardId)
    })
  }

  const handleSubmit = () => {
    if (isSaving || !hasChanges) {
      if (!hasChanges) {
        handleClose()
      }
      return
    }

    const trimmedNote = noteDraft.trim()
    let conformityNote: string | null = null
    if (selectedIds.length > 0 && trimmedNote.length > 0) {
      conformityNote = trimmedNote
    }

    updateMutation.mutate(
      {
        riskReductionMeasureId: riskReductionMeasure.id,
        ...toRiskReductionMeasureUpdateInput(riskReductionMeasure, {
          conformityStandardApplicabilityIds: selectedIds,
          conformityNote,
        }),
      },
      {
        onSuccess: () => {
          toast.success(t('riskReductionMeasuresLibraryPage.updateSuccess'))
          onClose()
        },
        onError: (error) => {
          toast.error(
            error.message ??
              t('riskReductionMeasuresLibraryPage.saveFailureUpdate')
          )
        },
      }
    )
  }

  const showEmptyState = !isLoadingApplicability && acceptedStandards.length === 0

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className='flex max-h-[85vh] flex-col gap-4 sm:max-w-2xl'
        showCloseButton={!isSaving}
      >
        <DialogHeader>
          <DialogTitle>
            {t(
              'riskReductionMeasuresLibraryPage.rrmDetail.editStandardConformityDialog.title'
            )}
          </DialogTitle>
        </DialogHeader>

        <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-hidden'>
          <div className='flex min-h-0 flex-1 flex-col space-y-2'>
            <Label>
              {t('riskReductionMeasuresLibraryPage.conformity.standardsLabel')}
            </Label>
            {isLoadingApplicability ? (
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <Loader2 className='size-4 animate-spin' />
                {t('common:loading')}
              </div>
            ) : null}
            {showEmptyState ? (
              <p className='text-sm text-muted-foreground'>
                {t('riskReductionMeasuresLibraryPage.conformity.emptyAcceptedStandards')}
              </p>
            ) : null}
            {!isLoadingApplicability && acceptedStandards.length > 0 ? (
              <div className='min-h-64 max-h-[min(24rem,50vh)] flex-1 space-y-3 overflow-y-auto rounded-md border p-4'>
                {acceptedStandards.map((standard) => {
                  const checkboxId = `rrm-conformity-standard-${standard.id}`
                  const isChecked = selectedIds.includes(standard.id)
                  return (
                    <div key={standard.id} className='flex items-start gap-3'>
                      <Checkbox
                        id={checkboxId}
                        checked={isChecked}
                        disabled={isSaving}
                        className='mt-0.5'
                        onCheckedChange={(checked) => {
                          toggleStandard(standard.id, checked === true)
                        }}
                      />
                      <Label
                        htmlFor={checkboxId}
                        className='flex cursor-pointer flex-col items-start gap-1 font-normal leading-relaxed'
                      >
                        <span className='font-medium leading-snug'>
                          {standard.reference}
                        </span>
                        {standard.title.trim().length > 0 ? (
                          <span className='text-sm font-normal text-muted-foreground'>
                            {standard.title}
                          </span>
                        ) : null}
                      </Label>
                    </div>
                  )
                })}
              </div>
            ) : null}
          </div>

          <div className='shrink-0 space-y-2'>
            <Label htmlFor='rrm-edit-standard-conformity-note'>
              {t('riskReductionMeasuresLibraryPage.conformity.noteLabel')}
            </Label>
            <Textarea
              id='rrm-edit-standard-conformity-note'
              value={noteDraft}
              onChange={(event) => {
                setNoteDraft(event.target.value)
              }}
              disabled={isSaving || selectedIds.length === 0}
              placeholder={t(
                'riskReductionMeasuresLibraryPage.conformity.notePlaceholder'
              )}
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose} disabled={isSaving}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || !hasChanges}>
            {isSaving ? <Loader2 className='size-4 animate-spin' /> : null}
            {t('riskReductionMeasuresLibraryPage.formDialog.updateButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
